import { Extension } from "@tiptap/core";
import { DOMSerializer, type Fragment } from "@tiptap/pm/model";
import { Plugin } from "@tiptap/pm/state";
import { preserveSpaces, restoreSpaces } from "@quill/shared";

/**
 * Leading spaces and runs of spaces show because the page is white-space: pre-wrap, which
 * copied HTML doesn't carry, so other editors would collapse them. Copy, cut, the Copy button
 * and dragging out all go through ProseMirror's clipboard serializer, so the rewrite happens
 * there (ProseMirror still deletes a cut, and the plain text is untouched); pasting back into
 * Quill turns the spaces back, so the document never gains non-breaking spaces from it.
 */
export const PreserveSpaces = Extension.create({
  name: "preserveSpaces",
  addProseMirrorPlugins() {
    const base = DOMSerializer.fromSchema(this.editor.schema);
    const serializer = new SpacePreservingSerializer(base.nodes, base.marks);
    return [
      new Plugin({
        props: {
          clipboardSerializer: serializer,
          transformPastedHTML: restoreSpaces,
        },
      }),
    ];
  },
});

class SpacePreservingSerializer extends DOMSerializer {
  override serializeFragment(
    fragment: Fragment,
    options: { document?: Document } = {},
    target?: HTMLElement | DocumentFragment,
  ): HTMLElement | DocumentFragment {
    // Called again for each node's content, into that node's element: rewrite only the whole.
    if (target) return super.serializeFragment(fragment, options, target);
    const doc = options.document ?? window.document;
    const box = doc.createElement("div");
    box.appendChild(super.serializeFragment(fragment, options));
    box.innerHTML = preserveSpaces(box.innerHTML);
    const out = doc.createDocumentFragment();
    while (box.firstChild) out.appendChild(box.firstChild);
    return out;
  }
}
